import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DailyStockTracker — May 2026.
 *
 * Collapsible per-ticker holdings table that lives at the bottom of the
 * My Portfolio card. Closed by default (chevron points DOWN); clicking
 * the header expands the table (chevron flips UP).
 *
 * Columns: Ticker · Shares · Price · Daily P/L · All-Time P/L · G/L %.
 * Every column is click-to-sort with an arrow indicating the active
 * column + direction. Default sort: ticker A→Z.
 *
 * Live behaviour: the row's `price`, `daily_pl_dollar`, `all_time_pl_dollar`,
 * and `gl_pct_total` are recomputed every render against the
 * `livePrices` map (keyed by ticker, supplied by the parent's
 * `usePriceStream` subscription). When the SSE pushes a new price for
 * a ticker, the math updates instantly without an extra API hit.
 *
 * Colouring: positive value or live price > previous close → emerald.
 * Negative → rose. Zero / unknown → muted.
 *
 * Props
 *   livePrices  - { [TICKER]: { price, change, ts } } from usePriceStream
 *   refreshKey  - bumps re-fetch of the base aggregated rows
 */
const COLUMNS = [
  { key: 'ticker',             label: 'Ticker',      align: 'left'  },
  { key: 'shares',             label: 'Shares',      align: 'right' },
  { key: 'price',              label: 'Price',       align: 'right' },
  { key: 'daily_pl_dollar',    label: 'Daily P/L',   align: 'right' },
  { key: 'all_time_pl_dollar', label: 'All-Time P/L',align: 'right' },
  { key: 'gl_pct_total',       label: 'G/L %',       align: 'right' },
];

function fmtMoney(n, { sign = false } = {}) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  const s = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (sign && n > 0) return `+$${s}`;
  if (n < 0) return `-$${s}`;
  return `$${s}`;
}

function fmtPct(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function fmtShares(n) {
  if (n === null || n === undefined) return '—';
  // Whole-share holdings render without decimals; fractional holdings
  // (DRIP rounding, broker partial fills) keep 2 decimals.
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2);
}

function toneFor(value, { invert = false } = {}) {
  if (value === null || value === undefined || Number.isNaN(value) || value === 0) {
    return 'text-muted-foreground';
  }
  const positive = invert ? value < 0 : value > 0;
  return positive
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-rose-600 dark:text-rose-400';
}

function SortArrow({ active, dir }) {
  if (!active) return null;
  // Compact 8px triangle; matches the chevron weight on the toggle button.
  return (
    <span className="ml-0.5 inline-block align-middle">
      {dir === 'desc' ? (
        <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
          <path d="M0 2 L8 2 L4 7 Z" fill="currentColor" />
        </svg>
      ) : (
        <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
          <path d="M0 6 L8 6 L4 1 Z" fill="currentColor" />
        </svg>
      )}
    </span>
  );
}

export default function DailyStockTracker({ livePrices, refreshKey = 0, onStockClick }) {
  const [rows, setRows] = useState([]);
  // `expanded` controls whether we render the first 3 rows (default)
  // or all rows. The table itself is ALWAYS visible — per user spec
  // we surface the first three lots immediately so the user lands on
  // the page already seeing live P/L; only the "show me more" gesture
  // is now gated. Previously a single boolean gated both visibility
  // AND fetch; we now fetch on mount unconditionally so the first
  // three rows are populated without waiting for a click.
  const [expanded, setExpanded] = useState(false);
  const [sortKey, setSortKey] = useState('ticker');
  const [sortDir, setSortDir] = useState('asc');
  const [loading, setLoading] = useState(true);
  // Round 92o++++++++N++++++++++ (Jun 2026) — Plus multi-portfolio.
  // Scope the table to the active portfolio so the rows match the
  // dropdown selection. Otherwise the user sees their entire
  // household of lots even when "ScopedView" should be empty.
  const scopeParams = usePortfolioScopeParams();
  const scopeKey = scopeParams.portfolio_id || 'all';

  // Fetch on mount + on refreshKey bump. No longer gated on open.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/portfolio/daily-tracker`, { params: scopeParams, withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey, scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when the user adds/sells/imports — the shares column
  // needs to stay in sync even if the table is collapsed to 3 rows.
  useEffect(() => {
    const h = () => {
      axios.get(`${API}/portfolio/daily-tracker`, { params: scopeParams, withCredentials: true })
        .then((res) => setRows(Array.isArray(res.data?.rows) ? res.data.rows : []))
        .catch(() => {});
    };
    window.addEventListener('portfolio-changed', h);
    window.addEventListener('cash-changed', h);
    return () => {
      window.removeEventListener('portfolio-changed', h);
      window.removeEventListener('cash-changed', h);
    };
  }, []);

  // Overlay live prices on top of the base rows. Recomputes every
  // tick because `livePrices` changes identity on each SSE diff.
  const liveRows = useMemo(() => {
    return rows.map((r) => {
      const live = livePrices?.[r.ticker]?.price;
      const livePx = typeof live === 'number' && live > 0 ? live : r.price;
      const shares = Number(r.shares) || 0;
      const avgCost = Number(r.avg_cost) || 0;
      const prevClose = Number(r.previous_close) || 0;
      const totalCost = shares * avgCost;
      const marketValue = shares * livePx;
      const allTimePl = marketValue - totalCost;
      const glPct = totalCost > 0 ? (allTimePl / totalCost) * 100 : 0;
      const dailyPl = (livePx > 0 && prevClose > 0) ? shares * (livePx - prevClose) : 0;
      const priceVsOpen = (livePx > 0 && prevClose > 0) ? livePx - prevClose : 0;
      return {
        ...r,
        price: livePx,
        daily_pl_dollar: dailyPl,
        all_time_pl_dollar: allTimePl,
        gl_pct_total: glPct,
        // Used to colour the Price cell red when current price is below
        // yesterday's close (per spec, even if other P/L fields are positive).
        _price_delta_vs_prev: priceVsOpen,
      };
    });
  }, [rows, livePrices]);

  const sortedRows = useMemo(() => {
    const out = [...liveRows];
    out.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const an = Number(av) || 0;
      const bn = Number(bv) || 0;
      return sortDir === 'asc' ? an - bn : bn - an;
    });
    return out;
  }, [liveRows, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Ticker defaults to A→Z; numeric columns default to highest first
      // (it's what users almost always want to see on a P/L sort).
      setSortDir(key === 'ticker' ? 'asc' : 'desc');
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-border" data-testid="daily-stock-tracker">
      {(() => {
        const totalRows = sortedRows.length;
        const hasMore = totalRows > 3;
        const visibleRows = expanded ? sortedRows : sortedRows.slice(0, 3);
        // Header is only a clickable expander when there are >3 rows
        // AND we're currently collapsed. Otherwise it's a plain label.
        const headerActsAsToggle = hasMore && !expanded;
        return (
          <>
            {headerActsAsToggle ? (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="mx-auto flex items-center gap-1.5 px-3 py-1 rounded-md text-[17px] font-semibold hover:bg-muted/40 transition-colors"
                data-testid="daily-tracker-toggle"
                aria-expanded={false}
                title={`Show all ${totalRows} positions`}
              >
                <span className="bg-gradient-to-b from-emerald-500 to-rose-500 bg-clip-text text-transparent">
                  $ Daily Stock Tracking
                </span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
              </button>
            ) : (
              <div
                className="mx-auto flex items-center gap-1.5 px-3 py-1 text-[17px] font-semibold"
                data-testid="daily-tracker-toggle"
                style={{ width: 'fit-content' }}
              >
                <span className="bg-gradient-to-b from-emerald-500 to-rose-500 bg-clip-text text-transparent">
                  $ Daily Stock Tracking
                </span>
              </div>
            )}

            {/* Portfolio-wide daily roll-up pill (Round 92m). Shows
                whenever there's at least one row in the live data so
                the user sees the headline number even in the default
                3-row collapsed state. Math uses every row (not just
                the visible three) so the pill represents the WHOLE
                portfolio, not just what's on screen. */}
            {sortedRows.length > 0 && (() => {
              let dailySum = 0;
              let prevCloseValueSum = 0;
              sortedRows.forEach((r) => {
                dailySum += Number(r.daily_pl_dollar) || 0;
                const shares = Number(r.shares) || 0;
                const prev = Number(r.previous_close) || 0;
                prevCloseValueSum += shares * prev;
              });
              const dailyPct = prevCloseValueSum > 0 ? (dailySum / prevCloseValueSum) * 100 : 0;
              const tone = toneFor(dailySum);
              const bgTone = dailySum > 0
                ? 'border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50/60 dark:bg-emerald-950/30'
                : dailySum < 0
                  ? 'border-rose-300/60 dark:border-rose-800/60 bg-rose-50/60 dark:bg-rose-950/30'
                  : 'border-border bg-muted/30';
              return (
                <div className="mt-3 flex justify-center" data-testid="daily-tracker-summary">
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[12px] font-semibold ${bgTone}`}>
                    <span className="text-muted-foreground uppercase tracking-wide text-[10px]">Today</span>
                    <span className={`font-mono font-bold ${tone}`} data-testid="daily-tracker-summary-dollar">
                      {fmtMoney(dailySum, { sign: true })}
                    </span>
                    <span className={`font-mono ${tone}`} data-testid="daily-tracker-summary-pct">
                      ({fmtPct(dailyPct)})
                    </span>
                  </div>
                </div>
              );
            })()}

            <div className="mt-3 overflow-x-auto" data-testid="daily-tracker-table-wrap">
              {loading && rows.length === 0 ? (
                <div className="text-center text-xs text-muted-foreground py-4">
                  Loading positions…
                </div>
              ) : sortedRows.length === 0 ? (
                <div className="text-center text-xs text-muted-foreground py-4">
                  No positions yet — add one to populate the tracker.
                </div>
              ) : (
                <table className="min-w-full text-[12px] font-mono" data-testid="daily-tracker-table">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      {COLUMNS.map((c) => (
                        <th
                          key={c.key}
                          className={`px-2 py-1.5 font-bold whitespace-nowrap ${c.align === 'right' ? 'text-right' : 'text-left'} cursor-pointer select-none hover:text-foreground transition-colors`}
                          onClick={() => handleSort(c.key)}
                          data-testid={`daily-tracker-th-${c.key}`}
                        >
                          <span className={`inline-flex items-center gap-0.5 ${c.align === 'right' ? 'justify-end' : ''} ${sortKey === c.key ? 'text-foreground' : ''}`}>
                            {c.label}
                            <SortArrow active={sortKey === c.key} dir={sortDir} />
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((r) => (
                      <tr
                        key={r.ticker}
                        className="border-t border-border/50 hover:bg-muted/20"
                        data-testid={`daily-tracker-row-${r.ticker}`}
                      >
                        <td className="px-2 py-1.5 text-left font-bold whitespace-nowrap" data-testid={`daily-tracker-ticker-${r.ticker}`}>
                          {onStockClick ? (
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onStockClick(r.ticker); }}
                              className="text-foreground hover:text-primary hover:underline cursor-pointer transition-colors"
                              data-testid={`daily-tracker-ticker-link-${r.ticker}`}
                              title={`Open ${r.ticker} details`}
                            >
                              {r.ticker}
                            </button>
                          ) : (
                            <span className="text-foreground">{r.ticker}</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right text-foreground whitespace-nowrap">
                          {fmtShares(r.shares)}
                        </td>
                        <td className={`px-2 py-1.5 text-right whitespace-nowrap ${toneFor(r._price_delta_vs_prev)}`} data-testid={`daily-tracker-price-${r.ticker}`}>
                          {r.price > 0 ? fmtMoney(r.price) : '—'}
                        </td>
                        <td className={`px-2 py-1.5 text-right whitespace-nowrap ${toneFor(r.daily_pl_dollar)}`} data-testid={`daily-tracker-daily-${r.ticker}`}>
                          {fmtMoney(r.daily_pl_dollar, { sign: true })}
                        </td>
                        <td className={`px-2 py-1.5 text-right whitespace-nowrap ${toneFor(r.all_time_pl_dollar)}`} data-testid={`daily-tracker-alltime-${r.ticker}`}>
                          {fmtMoney(r.all_time_pl_dollar, { sign: true })}
                        </td>
                        <td className={`px-2 py-1.5 text-right whitespace-nowrap ${toneFor(r.gl_pct_total)}`} data-testid={`daily-tracker-gl-${r.ticker}`}>
                          {fmtPct(r.gl_pct_total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Bottom toggle footer — Round 94g (Feb 2026):
                  • Expanded   → "Show Less ⌃"  (collapses back to 3)
                  • Collapsed  → "Show More ⌄"  (expands to full list)
                Only renders when there ARE more than 3 rows, otherwise
                this section is hidden entirely. The header arrow next
                to the "$ Daily Stock Tracking" title continues to act
                as the SAME toggle when the list is collapsed (per user
                spec — that arrow's behavior is unchanged). */}
            {hasMore && (
              <div className="mt-2 flex justify-center">
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-[13px] font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                  data-testid={expanded ? 'daily-tracker-show-less' : 'daily-tracker-show-more'}
                  title={expanded ? 'Collapse back to first 3 positions' : `Show all ${totalRows} positions`}
                >
                  {expanded ? 'Show Less' : 'Show More'}
                  {expanded
                    ? <ChevronUp className="w-4 h-4" aria-hidden="true" />
                    : <ChevronDown className="w-4 h-4" aria-hidden="true" />}
                </button>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}
